import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Factory, Building, MessageCircle, X } from 'lucide-react';
import { valueStreams, office } from '../data/factories';
import { useAuth } from '../contexts/AuthContext';
import DecryptedText from '../components/UI/DecryptedText';
import ChatInterface from '../components/Chat/ChatInterface';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [showChat, setShowChat] = useState(false);
  const username = currentUser?.email?.split('@')[0];

  // Filter out the Services section from valueStreams
  const services = valueStreams.find(vs => vs.id === 'services');
  const vsStreams = valueStreams.filter(vs => vs.id !== 'services');

  return (
    <div className="animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          <DecryptedText 
            text={`Bienvenue, ${username}`}
            animateOn="view"
            speed={100}
            sequential={true}
            revealDirection="center"
            className="text-gray-900 dark:text-white"
            encryptedClassName="text-primary/60"
          />
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
          <DecryptedText 
            text="Accédez aux enregistrements des réunions pour chaque usine de Groupe Savoie"
            animateOn="view"
            speed={50}
            sequential={true}
            className="text-gray-600 dark:text-gray-300"
            encryptedClassName="text-primary/40"
          />
        </p>
      </div>

      {/* Office Section */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
          <DecryptedText 
            text="Administration"
            animateOn="view"
            speed={100}
            sequential={true}
            className="text-gray-900 dark:text-white"
            encryptedClassName="text-primary/60"
          />
        </h2>
        
        <Link 
          to={`/usine/${office.id}`}
          className="block bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-1"
        >
          <div className="h-16 sm:h-20 md:h-24 bg-primary flex items-center justify-center">
            <Building size={32} className="sm:hidden text-white" />
            <Building size={40} className="hidden sm:block md:hidden text-white" />
            <Building size={48} className="hidden md:block text-white" />
          </div>
          
          <div className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {office.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {office.description}
            </p>
          </div>
        </Link>
      </div>

      {/* Services Section */}
      {services && (
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
            <DecryptedText 
              text="Services"
              animateOn="view"
              speed={100}
              sequential={true}
              className="text-gray-900 dark:text-white"
              encryptedClassName="text-primary/60"
            />
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services.factories.map((factory) => (
              <Link 
                key={factory.id}
                to={`/usine/${factory.id}`}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-1"
              >
                <div className="h-16 sm:h-20 bg-primary flex items-center justify-center">
                  <Factory size={28} className="sm:hidden text-white" />
                  <Factory size={32} className="hidden sm:block md:hidden text-white" />
                  <Factory size={36} className="hidden md:block text-white" />
                </div>
                
                <div className="p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-1">
                    {factory.name}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                    {factory.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Value Streams Section */}
      <div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
          <DecryptedText 
            text="Value Streams"
            animateOn="view"
            speed={100}
            sequential={true}
            className="text-gray-900 dark:text-white"
            encryptedClassName="text-primary/60"
          />
        </h2>
        
        <div className="space-y-6 sm:space-y-8">
          {vsStreams.map((vs) => (
            <div key={vs.id}>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">
                <DecryptedText 
                  text={vs.name}
                  animateOn="view"
                  speed={100}
                  sequential={true}
                  className="text-gray-900 dark:text-white"
                  encryptedClassName="text-primary/60"
                />
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {vs.factories.map((factory) => (
                  <Link 
                    key={factory.id}
                    to={`/usine/${factory.id}`}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-1"
                  >
                    <div className="h-16 sm:h-20 bg-primary flex items-center justify-center">
                      <Factory size={28} className="sm:hidden text-white" />
                      <Factory size={32} className="hidden sm:block md:hidden text-white" />
                      <Factory size={36} className="hidden md:block text-white" />
                    </div>
                    
                    <div className="p-3 sm:p-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-1">
                        {factory.name}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                        {factory.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Button */}
      <button
        onClick={() => setShowChat(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary hover:bg-primary-dark text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-40"
        aria-label="Ouvrir l'assistant technique"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] max-h-[600px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Assistant Technique
              </h2>
              <button
                onClick={() => setShowChat(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ChatInterface onClose={() => setShowChat(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;